import {
  Badge,
  ChannelNotice,
  Button,
  Card,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
} from "@forge/ui";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, MessageCircleQuestion } from "lucide-react";
import { MobileInboxPane } from "../../components/mobile-inbox-pane";
import { QuestionThread } from "../../components/question-thread";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { channelCopy, useChannels } from "../../lib/channel";
import { formatDateTime } from "../../lib/format";
import { QUESTION_STATUS_LABELS } from "../../lib/order-status";

const PAGE_SIZE = 25;

type QuestionsSearch = {
  page: number;
  status?: string;
  accountId?: string;
  open?: string;
};

export const Route = createFileRoute("/_app/questions")({
  validateSearch: (search: Record<string, unknown>): QuestionsSearch => ({
    page: Number(search.page ?? 1) || 1,
    status: typeof search.status === "string" ? search.status : "unanswered",
    accountId:
      typeof search.accountId === "string" ? search.accountId : undefined,
    open: typeof search.open === "string" ? search.open : undefined,
  }),
  component: QuestionsPage,
});

function QuestionsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const channels = useChannels();
  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });

  const questions = useQuery({
    queryKey: ["questions", search],
    queryFn: async () =>
      unwrap(
        await api.questions.get({
          query: {
            offset: (search.page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            status:
              search.status && search.status !== "all"
                ? (search.status as never)
                : undefined,
            accountId: search.accountId,
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const total = questions.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const writesEnabled = questions.data?.writesEnabled ?? false;
  const selected =
    questions.data?.questions.find((row) => row.id === search.open) ?? null;

  function closeThread() {
    void navigate({
      search: (prev) => ({ ...prev, open: undefined }),
    });
  }

  function refreshQuestion(question: {
    id: string;
    listingId?: string | null;
  }) {
    void queryClient.invalidateQueries({ queryKey: ["questions"] });
    if (question.listingId) {
      void queryClient.invalidateQueries({
        queryKey: ["listings", question.listingId],
      });
    }
  }

  return (
    <>
      <PageHeader
        title="Perguntas"
        description={`${total} perguntas ${search.status === "unanswered" ? "sem resposta" : "neste filtro"}`}
        actions={
          <>
            <Select
              value={search.status ?? null}
              itemToStringLabel={(value) =>
                value == null || value === "all"
                  ? "Todos os status"
                  : (QUESTION_STATUS_LABELS[value] ?? String(value))
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    status: (value as string) ?? "all",
                    page: 1,
                  }),
                })
              }
            >
              <SelectTrigger placeholder="Todos os status" />
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.entries(QUESTION_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.accountId ?? null}
              itemToStringLabel={(value) =>
                value == null
                  ? "Todas as contas"
                  : (accounts.data?.accounts.find(
                      (account) => account.id === value,
                    )?.displayName ?? String(value))
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    accountId: (value as string) ?? undefined,
                    page: 1,
                  }),
                })
              }
            >
              <SelectTrigger placeholder="Todas as contas" />
              <SelectContent>
                <SelectItem value={null}>Todas as contas</SelectItem>
                {accounts.data?.accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      {channels.hasChannel("shopee") ? (
        <ChannelNotice provider="shopee" className="mb-3">
          {channelCopy("shopee").questionsNote}
        </ChannelNotice>
      ) : null}

      <Card>
        {questions.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="size-5 text-ink-muted" />
          </div>
        ) : questions.data && questions.data.questions.length > 0 ? (
          <>
            <ul className="hidden md:block">
              {questions.data.questions.map((question, index) => (
                <li
                  key={question.id}
                  className={
                    index < questions.data.questions.length - 1
                      ? "border-b border-line"
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between gap-3 px-4 pt-3">
                    <div className="min-w-0">
                      {question.listingId ? (
                        <Link
                          to="/listings/$id"
                          params={{ id: question.listingId }}
                          className="truncate text-2xs font-medium text-ink hover:text-ember"
                        >
                          {question.listingTitle ?? question.externalItemId}
                        </Link>
                      ) : (
                        <p className="truncate text-2xs font-medium text-ink">
                          {question.listingTitle ?? question.externalItemId}
                        </p>
                      )}
                    </div>
                    <Badge variant="neutral">{question.accountNickname}</Badge>
                  </div>
                  <QuestionThread
                    question={question}
                    writesEnabled={writesEnabled}
                    className="border-b-0"
                    onAnswered={() => refreshQuestion(question)}
                  />
                </li>
              ))}
            </ul>
            <ul className="divide-y divide-line md:hidden">
              {questions.data.questions.map((question) => (
                <li key={question.id}>
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left active:scale-[0.99] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken"
                    onClick={() =>
                      void navigate({
                        search: (prev) => ({ ...prev, open: question.id }),
                      })
                    }
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-2xs font-medium text-ink">
                        {question.listingTitle ?? question.externalItemId}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-[0.8125rem] text-ink">
                        {question.questionText}
                      </p>
                      <p className="mt-1 text-2xs text-ink-faint">
                        {question.askerNickname ?? "Comprador"} ·{" "}
                        {formatDateTime(question.askedAt)}
                      </p>
                    </div>
                    <Badge variant="neutral">{question.accountNickname}</Badge>
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between border-t border-line px-3 py-2">
              <p className="text-2xs text-ink-muted">
                Página {search.page} de {pageCount}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={search.page <= 1}
                  onClick={() =>
                    navigate({
                      search: (prev) => ({ ...prev, page: search.page - 1 }),
                    })
                  }
                >
                  <ChevronLeft />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={search.page >= pageCount}
                  onClick={() =>
                    navigate({
                      search: (prev) => ({ ...prev, page: search.page + 1 }),
                    })
                  }
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            icon={<MessageCircleQuestion />}
            title="Nenhuma pergunta neste filtro"
            description="Importe as perguntas em Conexões ou aguarde o aviso do Mercado Livre."
          />
        )}
      </Card>

      <MobileInboxPane
        open={Boolean(search.open)}
        title={
          selected?.listingTitle ?? selected?.externalItemId ?? "Pergunta"
        }
        subtitle={
          selected
            ? `${selected.askerNickname ?? "Comprador"} · ${selected.accountNickname}`
            : undefined
        }
        onClose={closeThread}
      >
        {selected ? (
          <QuestionThread
            variant="sheet"
            question={selected}
            writesEnabled={writesEnabled}
            onAnswered={() => {
              refreshQuestion(selected);
              closeThread();
            }}
          />
        ) : (
          <p className="px-4 py-6 text-2xs text-ink-muted">
            Esta pergunta não está nesta página.
          </p>
        )}
      </MobileInboxPane>
    </>
  );
}
